import { useState, useEffect } from "react";
import {
    TextField, Box, Checkbox, FormControl, Select,
    MenuItem, InputLabel, FormControlLabel, CircularProgress, Button,
    Divider, Chip, Alert,
    Typography
} from "@mui/material";
import { usePostDataFetch, usePutDataFetch } from "../../utils/useDataFetch";

const WOCHENTAGE = [
    { value: 'montag', label: 'Montag' },
    { value: 'dienstag', label: 'Dienstag' },
    { value: 'mittwoch', label: 'Mittwoch' },
    { value: 'donnerstag', label: 'Donnerstag' },
    { value: 'freitag', label: 'Freitag' },
    { value: 'samstag', label: 'Samstag' },
    { value: 'sonntag', label: 'Sonntag' },
];

const CreateTermin = ({ initialData = null, onSucess = () => { }, activeTeamId = null }) => {
    const { error: postError, isLoading: postLoading, postData: postFetch } = usePostDataFetch();
    const { error: putError, isLoading: putLoading, putData: putFetch } = usePutDataFetch();

    const [newTermin, setNewTermin] = useState({
        titel: "",
        beschreibung: "",
        start_time: "",
        end_time: "",
        dat: "",
        start_dat: "",
        end_dat: "",
        ort: "",
        typ: "Training",
        is_recurring: false,
        wochentag: "",
        deadline_value: 0,
        deadline_unit: "hours"
    });

    useEffect(() => {
        if (initialData) {
            setNewTermin({
                ...initialData,
                dat: initialData.dat ? new Date(initialData.dat).toISOString().split('T')[0] : "",
                start_dat: initialData.start_dat ? new Date(initialData.start_dat).toISOString().split('T')[0] : "",
                end_dat: initialData.end_dat ? new Date(initialData.end_dat).toISOString().split('T')[0] : "",
                deadline_value: 0, // Reset for simplicity or calculate from existing deadline if possible
                deadline_unit: "hours"
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewTermin((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (e) => {
        setNewTermin((prev) => ({
            ...prev,
            is_recurring: e.target.checked
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            ...newTermin,
            team_id: activeTeamId,
            beschreibung: newTermin.beschreibung || null,
            ort: newTermin.ort || null,
            dat: newTermin.is_recurring ? newTermin.start_dat : newTermin.dat,
            start_dat: newTermin.is_recurring ? newTermin.start_dat : newTermin.dat,
            end_dat: newTermin.is_recurring ? newTermin.end_dat : newTermin.dat,
            wochentag: newTermin.is_recurring ? newTermin.wochentag : null,
        };

        if (initialData?.id) {
            await putFetch({ url: `/termine/${initialData.id}`, aBody: payload });
        } else {
            await postFetch({ url: "/termine", aBody: payload });
        }
        onSucess();
    };

    const isLoading = postLoading || putLoading;
    const error = postError || putError;

    if (isLoading) {
        return (
            <CircularProgress />
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <TextField required name="titel" label="Titel" value={newTermin.titel} onChange={handleChange} fullWidth />

                    <FormControl fullWidth>
                        <InputLabel>Typ</InputLabel>
                        <Select name="typ" value={newTermin.typ} label="Typ" onChange={handleChange}>
                            <MenuItem value="Training">Training</MenuItem>
                            <MenuItem value="Spiel">Spiel</MenuItem>
                            <MenuItem value="Event">Event</MenuItem>
                            <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField name="beschreibung" label="Beschreibung" multiline rows={2} value={newTermin.beschreibung} onChange={handleChange} fullWidth />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField required name="start_time" label="Startzeit" type="time" InputLabelProps={{ shrink: true }} value={newTermin.start_time} onChange={handleChange} fullWidth />
                        <TextField name="end_time" label="Endzeit" type="time" InputLabelProps={{ shrink: true }} value={newTermin.end_time} onChange={handleChange} fullWidth />
                    </Box>

                    {newTermin.is_recurring ? (
                        <>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField required name="start_dat" label="Gültig ab" type="date" InputLabelProps={{ shrink: true }} value={newTermin.start_dat} onChange={handleChange} fullWidth />
                                <TextField name="end_dat" label="Gültig bis (optional)" type="date" InputLabelProps={{ shrink: true }} value={newTermin.end_dat} onChange={handleChange} fullWidth />
                            </Box>

                            <FormControl required fullWidth>
                                <InputLabel>Wochentag</InputLabel>
                                <Select name="wochentag" value={newTermin.wochentag} label="Wochentag" onChange={handleChange}>
                                    {WOCHENTAGE.map((tag) => (
                                        <MenuItem key={tag.value} value={tag.value}>
                                            {tag.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    ) : (
                        <TextField required name="dat" label="Datum" type="date" InputLabelProps={{ shrink: true }} value={newTermin.dat} onChange={handleChange} fullWidth />
                    )}

                    <TextField name="ort" label="Ort / Treffpunkt" value={newTermin.ort} onChange={handleChange} fullWidth />

                    <Divider><Chip label="Abmeldefrist (Optional)" size="small" /></Divider>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            name="deadline_value"
                            label="Frist vor Beginn"
                            type="number"
                            value={newTermin.deadline_value}
                            onChange={handleChange}
                            sx={{ flexGrow: 1 }}
                        />
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Einheit</InputLabel>
                            <Select name="deadline_unit" value={newTermin.deadline_unit} label="Einheit" onChange={handleChange}>
                                <MenuItem value="minutes">Minuten</MenuItem>
                                <MenuItem value="hours">Stunden</MenuItem>
                                <MenuItem value="days">Tage</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <FormControlLabel
                        control={<Checkbox checked={newTermin.is_recurring} onChange={handleCheckboxChange} />}
                        label="Wiederkehrender Folgetermin"
                    />

                    <Button variant="contained" type="submit" size="large" sx={{ mt: 1, borderRadius: "12px" }}>
                        {initialData ? "Änderungen speichern" : "Termin erstellen"}
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
    );
};

export default CreateTermin;
